<template>
    <div class="sale-differe-view">
        <div class="page-header">
            <div class="flex">
                <button @click="goBack" class="back-btn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
                <div class="page-title">
                    <h1 class="page-heading">Vente Différée</h1>
                    <p class="page-subtitle">Vente avec paiement ultérieur</p>
                </div>
            </div>
            <button @click="validateSale" class="validate-cart-btn" :disabled="!canValidate || isProcessing">
                <span v-if="isProcessing" class="spinner-small"></span>
                <template v-else>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Créer la vente différée
                </template>
            </button>
        </div>

        <div class="sale-container">
            <div class="products-column">
                <div class="search-container">
                    <div class="search-input-wrapper">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input ref="searchInput" type="text" v-model="searchQuery"
                            placeholder="Rechercher un produit (Nom, Code-barres, Réf)..." @input="searchProducts"
                            @keydown.down.prevent="navigateProducts('down')"
                            @keydown.up.prevent="navigateProducts('up')"
                            @keydown.enter.prevent="selectHighlightedProduct" class="search-input" />
                        <button v-if="searchQuery" @click="clearSearch" class="clear-search-btn">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    <div class="categories-filter">
                        <select v-model="selectedCategory" @change="filterByCategory">
                            <option value="">Toutes les catégories</option>
                            <option v-for="category in categories" :key="category.id" :value="category.id">
                                {{ category.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="products-list">
                    <div v-if="loading" class="loading-container">
                        <div class="spinner"></div>
                        <p>Chargement des produits...</p>
                    </div>

                    <div v-else-if="filteredProducts.length === 0" class="empty-results">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M20 7l-8-4-8 4M4 7v10l8 4M12 7v10M12 7v10m8-10v10l-8 4"></path>
                        </svg>
                        <p>Aucun produit trouvé</p>
                        <button v-if="searchQuery" @click="clearSearch" class="clear-search-link">
                            Effacer la recherche
                        </button>
                    </div>

                    <div v-else class="products-table-container">
                        <table class="products-table">
                            <thead>
                                <tr>
                                    <th class="col-image">Image</th>
                                    <th class="col-name">Produit</th>
                                    <th class="col-category">Catégorie</th>
                                    <th class="col-stock">Stock</th>
                                    <th class="col-price">Prix</th>
                                    <th class="col-action"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(product, index) in filteredProducts" :key="product.id" class="product-row"
                                    :class="{ 'highlighted': highlightedProductIndex === index }"
                                    @click="addToCart(product)" @mouseenter="highlightedProductIndex = index">
                                    <td class="col-image">
                                        <div class="product-image-small">
                                            <img v-if="product.image_url" :src="product.image_url"
                                                :alt="product.name" />
                                            <div v-else class="placeholder-image-small">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                    <polyline points="21 15 16 10 5 21"></polyline>
                                                </svg>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="col-name">
                                        <span class="product-name-text">{{ product.name }}</span>
                                        <span v-if="product.reference" class="product-reference">Réf: {{
                                            product.reference }}</span>
                                    </td>
                                    <td class="col-category">
                                        <span class="category-badge">{{ product.category?.name || '-' }}</span>
                                    </td>
                                    <td class="col-stock">
                                        <span class="stock-badge" :class="getStockClass(product.quantity)">
                                            {{ product.quantity || 0 }}
                                        </span>
                                    </td>
                                    <td class="col-price">
                                        <span class="product-price-text">{{ formatCurrency(product.price_incl_tax)
                                        }}</span>
                                    </td>
                                    <td class="col-action">
                                        <button class="add-btn" @click.stop="addToCart(product)">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round">
                                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="cart.length > 0" class="quick-actions-bar">
                    <div class="quick-info">
                        <span class="items-count">{{ cart.length }} article(s)</span>
                        <span class="quick-total">Total: {{ formatCurrency(total) }}</span>
                    </div>
                    <button @click="focusCustomer" class="quick-validate-btn">
                        Finaliser
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </button>
                </div>
            </div>

            <div class="cart-column">
                <div class="cart-header">
                    <h2>Panier ({{ cart.length }})</h2>
                    <button v-if="cart.length > 0" @click="clearCart" class="clear-cart-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2">
                            </path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                        Vider
                    </button>
                </div>

                <div v-if="cart.length === 0" class="empty-cart">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="9" cy="21" r="1"></circle>
                        <circle cx="20" cy="21" r="1"></circle>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                    </svg>
                    <p>Votre panier est vide</p>
                    <p class="empty-cart-hint">Cliquez sur un produit ou utilisez la recherche</p>
                </div>

                <div v-else class="cart-items">
                    <div v-for="(item, index) in cart" :key="index" class="cart-item">
                        <div class="item-details">
                            <div class="item-name">{{ item.name }}</div>
                            <div class="item-price">
                                <input type="number" class="price-input" v-model.number="item.price_incl_tax" min="0"
                                    step="1" @input="onUnitPriceInput(index)" @focus="$event.target.select()" />
                                × {{ item.quantity }}
                            </div>
                        </div>
                        <div class="item-actions">
                            <div class="item-quantity">
                                <button @click="decrementQuantity(index)" class="quantity-btn">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                </button>
                                <input type="number" v-model.number="item.quantity" min="1"
                                    @change="updateItemTotal(index)" @focus="$event.target.select()">
                                <button @click="incrementQuantity(index)" class="quantity-btn">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                </button>
                            </div>
                            <div class="item-total">{{ formatCurrency(item.total) }}</div>
                            <button @click="removeFromCart(index)" class="remove-item-btn">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="cart.length > 0" class="cart-summary">
                    <div class="customer-section" ref="customerSection">
                        <label class="customer-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            Client (obligatoire) *
                        </label>
                        <div class="customer-options">
                            <button :class="['customer-option-btn', { 'active': customerType === 'existing' }]"
                                @click="selectCustomerType('existing')">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                                Client existant
                            </button>
                            <button :class="['customer-option-btn', { 'active': customerType === 'new' }]"
                                @click="selectCustomerType('new')">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <line x1="19" y1="8" x2="19" y2="14"></line>
                                    <line x1="22" y1="11" x2="16" y2="11"></line>
                                </svg>
                                Nouveau client
                            </button>
                        </div>

                        <div v-if="customerType === 'existing'" class="existing-customer-selector">
                            <div class="customer-search-container">
                                <div class="customer-search-input-wrapper">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                    <input ref="customerSearchInput" type="text" v-model="customerSearchQuery"
                                        @input="searchCustomers" @focus="showCustomerDropdown = true"
                                        placeholder="Rechercher un client..." class="customer-search-input" />
                                    <button v-if="customerSearchQuery" @click="clearCustomerSearch"
                                        class="clear-search-btn">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                    </button>
                                </div>

                                <div v-if="showCustomerDropdown && filteredCustomers.length > 0"
                                    class="customer-dropdown">
                                    <div v-for="customer in filteredCustomers" :key="customer.id"
                                        @click="selectCustomer(customer)" class="customer-option">
                                        <div class="customer-info">
                                            <div class="customer-name">{{ customer.name }}</div>
                                            <div v-if="customer.phone" class="customer-phone">{{ customer.phone }}</div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="showCustomerDropdown && filteredCustomers.length === 0 && customerSearchQuery.trim()"
                                    class="no-customers-found">
                                    <p>Aucun client trouvé</p>
                                </div>
                            </div>

                            <div v-if="selectedCustomer" class="selected-customer-card">
                                <div class="customer-details">
                                    <div class="customer-name">{{ selectedCustomer.name }}</div>
                                    <div v-if="selectedCustomer.phone" class="customer-phone">{{ selectedCustomer.phone
                                    }}</div>
                                </div>
                                <button @click="clearCustomerSelection" class="remove-customer-btn">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div v-if="customerType === 'new'" class="new-customer-form">
                            <div class="form-group">
                                <label>Nom du client *</label>
                                <input ref="newCustomerNameInput" type="text" v-model="newCustomer.name"
                                    placeholder="Nom complet" required @focus="$event.target.select()">
                            </div>
                            <div class="form-group">
                                <label>Téléphone</label>
                                <input type="tel" v-model="newCustomer.phone" placeholder="Téléphone"
                                    @focus="$event.target.select()">
                            </div>
                            <div class="form-group">
                                <label>Email</label>
                                <input type="email" v-model="newCustomer.email" placeholder="Email"
                                    @focus="$event.target.select()">
                            </div>
                        </div>
                    </div>

                    <div class="summary-row subtotal-row">
                        <span>Sous-total</span>
                        <span class="subtotal-amount">{{ formatCurrency(subtotal) }}</span>
                    </div>

                    <div class="summary-row discount-row">
                        <label class="discount-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            Remise
                        </label>
                        <div class="discount-input">
                            <input type="number" v-model.number="discount" min="0" :max="subtotal" placeholder="0"
                                @input="calculateTotal" @focus="$event.target.select()">
                            <span class="currency-symbol">FCFA</span>
                        </div>
                    </div>

                    <div class="summary-row total-row">
                        <span>Total à payer</span>
                        <span class="total-amount">{{ formatCurrency(total) }}</span>
                    </div>


                </div>
            </div>
        </div>

        <div v-if="showSuccessModal" class="modal-overlay" @click.self="goBack">
            <div class="modal-container success-modal">
                <div class="modal-body text-center">
                    <div class="success-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    </div>
                    <h2>Vente différée créée avec succès !</h2>
                    <p>Facture N° <span class="invoice-number">{{ completedSale.invoice_number }}</span></p>
                    <div class="success-details">
                        <div class="detail-row">
                            <span>Client</span>
                            <span class="detail-value">{{ completedSale.customer?.name }}</span>
                        </div>
                        <div class="detail-row">
                            <span>Articles</span>
                            <span class="detail-value">{{ completedSale.items?.length || cart.length }}</span>
                        </div>
                    </div>
                    <div class="success-total">
                        <span>Total</span>
                        <span class="success-amount">{{ formatCurrency(completedSale.total_incl_tax) }}</span>
                    </div>
                </div>

                <div class="modal-footer">
                    <div class="action-cards">
                        <button @click="printReceipt(completedSale.id)" class="action-card">
                            <div class="action-card-icon invoice-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                    <polyline points="10 9 9 9 8 9"></polyline>
                                </svg>
                            </div>
                            <span class="action-card-label">Facture</span>
                        </button>
                        <button @click="printTicket(completedSale)" class="action-card">
                            <div class="action-card-icon ticket-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                    <path
                                        d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2">
                                    </path>
                                    <rect x="6" y="14" width="12" height="8"></rect>
                                </svg>
                            </div>
                            <span class="action-card-label">Ticket</span>
                        </button>
                        <button @click="newSale" class="action-card primary">
                            <div class="action-card-icon new-sale-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </div>
                            <span class="action-card-label">Nouvelle vente</span>
                        </button>
                    </div>
                    <button @click="goBack" class="back-link">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12"></line>
                            <polyline points="12 19 5 12 12 5"></polyline>
                        </svg>
                        Retour aux ventes
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { productsService, salesService, categoriesService } from '@/services'
import customerService from '@/services/customerService'
import CashRegisterService from '@/services/cashRegisterService'
import { useToastStore } from '@/store/modules/toastStore'
import thermalPrinterService from '@/services/thermalPrinterService'

export default defineComponent({
    name: 'SaleDiffere',
    setup() {
        const router = useRouter()
        const toastStore = useToastStore()

        const loading = ref(true)
        const products = ref([])
        const categories = ref([])
        const searchQuery = ref('')
        const selectedCategory = ref('')
        const cart = ref([])
        const discount = ref(0)
        const isProcessing = ref(false)
        const showSuccessModal = ref(false)
        const completedSale = ref({})
        const highlightedProductIndex = ref(-1)
        const searchInput = ref(null)
        const searchTimeout = ref(null)

        const customerType = ref('existing')
        const selectedCustomer = ref(null)
        const newCustomer = ref({ name: '', phone: '', email: '' })
        const customerSearchQuery = ref('')
        const filteredCustomers = ref([])
        const showCustomerDropdown = ref(false)
        const customers = ref([])
        const activeCashRegister = ref(null)
        const customerSection = ref(null)
        const customerSearchInput = ref(null)
        const newCustomerNameInput = ref(null)

        const filteredProducts = computed(() => {
            let result = [...products.value]
            if (selectedCategory.value) {
                result = result.filter(product => product.category_id === selectedCategory.value)
            }
            return result
        })

        const subtotal = computed(() => {
            return cart.value.reduce((sum, item) => sum + item.total, 0)
        })

        const total = computed(() => {
            return Math.max(0, subtotal.value - (discount.value || 0))
        })

        const canValidate = computed(() => {
            if (cart.value.length === 0) return false

            if (customerType.value === 'existing') {
                return selectedCustomer.value !== null
            }

            if (customerType.value === 'new') {
                return newCustomer.value.name.trim() !== ''
            }

            return false
        })

        const loadProducts = async () => {
            try {
                loading.value = true
                const response = await productsService.getProducts({
                    per_page: 50,
                    with: ['category'],
                    search: searchQuery.value
                })
                products.value = response.data.data || response.data || []
                highlightedProductIndex.value = products.value.length > 0 ? 0 : -1
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des produits',
                    duration: 4000
                })
            } finally {
                loading.value = false
            }
        }

        const loadCategories = async () => {
            try {
                const response = await categoriesService.getCategories()
                categories.value = response.data.data || response.data || []
            } catch (error) {
                console.error('Erreur lors du chargement des catégories:', error)
            }
        }

        const loadCustomers = async () => {
            try {
                const response = await customerService.getCustomersForSelect()
                customers.value = response.data.data || response.data || []
                filteredCustomers.value = customers.value
            } catch (error) {
                console.error('Erreur lors du chargement des clients:', error)
                customers.value = []
                filteredCustomers.value = []
            }
        }

        const loadActiveCashRegister = async () => {
            try {
                const response = await CashRegisterService.getCashRegisters({ status: 'open' })
                const openRegisters = response.data.data || response.data || []
                if (openRegisters.length > 0) {
                    activeCashRegister.value = openRegisters[0]
                } else {
                    toastStore.addToast({
                        type: 'error',
                        message: 'Aucune caisse ouverte. Veuillez ouvrir une caisse avant de faire une vente.',
                        duration: 5000
                    })
                    router.push({ name: 'cash-registers' })
                }
            } catch (error) {
                console.error('Erreur lors du chargement de la caisse active:', error)
            }
        }

        const searchProducts = () => {
            if (searchTimeout.value) {
                clearTimeout(searchTimeout.value)
            }
            searchTimeout.value = setTimeout(() => {
                loadProducts()
            }, 300)
        }

        const clearSearch = () => {
            searchQuery.value = ''
            loadProducts()
            nextTick(() => {
                searchInput.value?.focus()
            })
        }

        const navigateProducts = (direction) => {
            if (filteredProducts.value.length === 0) return

            if (direction === 'down') {
                highlightedProductIndex.value = (highlightedProductIndex.value + 1) % filteredProducts.value.length
            } else if (direction === 'up') {
                highlightedProductIndex.value = highlightedProductIndex.value <= 0
                    ? filteredProducts.value.length - 1
                    : highlightedProductIndex.value - 1
            }

            const highlightedRow = document.querySelector('.product-row.highlighted')
            if (highlightedRow) {
                highlightedRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }
        }

        const selectHighlightedProduct = () => {
            if (highlightedProductIndex.value >= 0 && highlightedProductIndex.value < filteredProducts.value.length) {
                addToCart(filteredProducts.value[highlightedProductIndex.value])
            }
        }

        const filterByCategory = () => {
            highlightedProductIndex.value = 0
        }

        const addToCart = (product) => {
            const existingItemIndex = cart.value.findIndex(item => item.id === product.id)

            if (existingItemIndex !== -1) {
                cart.value[existingItemIndex].quantity += 1
                updateItemTotal(existingItemIndex)
                toastStore.addToast({
                    type: 'success',
                    message: `${product.name} ajouté au panier`,
                    duration: 2000
                })
            } else {
                const cartItem = {
                    id: product.id,
                    name: product.name,
                    price_excl_tax: product.price_excl_tax,
                    price_incl_tax: product.price_incl_tax,
                    quantity: 1,
                    tax_rate: product.tax_rate,
                    total: product.price_incl_tax
                }
                cart.value.push(cartItem)
                toastStore.addToast({
                    type: 'success',
                    message: `${product.name} ajouté au panier`,
                    duration: 2000
                })
            }

            nextTick(() => {
                searchInput.value?.focus()
            })
        }

        const updateItemTotal = (index) => {
            if (index >= 0 && index < cart.value.length) {
                const item = cart.value[index]
                if (item.quantity < 1) {
                    item.quantity = 1
                }
                item.total = item.price_incl_tax * item.quantity
            }
        }

        const onUnitPriceInput = (index) => {
            if (index < 0 || index >= cart.value.length) return
            const item = cart.value[index]
            const taxRate = Number(item.tax_rate) || 0
            if (!Number.isFinite(item.price_incl_tax) || item.price_incl_tax < 0) {
                item.price_incl_tax = 0
            }
            const divisor = 1 + (taxRate / 100)
            item.price_excl_tax = Math.round((item.price_incl_tax / divisor) * 100) / 100
            updateItemTotal(index)
        }

        const incrementQuantity = (index) => {
            if (index >= 0 && index < cart.value.length) {
                cart.value[index].quantity += 1
                updateItemTotal(index)
            }
        }

        const decrementQuantity = (index) => {
            if (index >= 0 && index < cart.value.length && cart.value[index].quantity > 1) {
                cart.value[index].quantity -= 1
                updateItemTotal(index)
            }
        }

        const removeFromCart = (index) => {
            if (index >= 0 && index < cart.value.length) {
                cart.value.splice(index, 1)
                toastStore.addToast({
                    type: 'info',
                    message: 'Article retiré du panier',
                    duration: 2000
                })
            }
        }

        const clearCart = () => {
            if (confirm('Êtes-vous sûr de vouloir vider le panier ?')) {
                cart.value = []
                discount.value = 0
                toastStore.addToast({
                    type: 'info',
                    message: 'Panier vidé',
                    duration: 2000
                })
            }
        }

        const calculateTotal = () => {
            if (discount.value > subtotal.value) {
                discount.value = subtotal.value
            }
        }

        const focusCustomer = () => {
            if (customerSection.value) {
                customerSection.value.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

                setTimeout(() => {
                    if (customerType.value === 'existing') {
                        customerSearchInput.value?.focus()
                    } else if (customerType.value === 'new') {
                        newCustomerNameInput.value?.focus()
                    }
                }, 300)
            }
        }

        const selectCustomerType = (type) => {
            customerType.value = type
            if (type === 'existing') {
                loadCustomers()
                nextTick(() => {
                    customerSearchInput.value?.focus()
                })
            } else if (type === 'new') {
                nextTick(() => {
                    newCustomerNameInput.value?.focus()
                })
            }
        }

        const searchCustomers = () => {
            if (!customerSearchQuery.value.trim()) {
                filteredCustomers.value = customers.value
                showCustomerDropdown.value = false
                return
            }

            const query = customerSearchQuery.value.toLowerCase()

            customerService.getCustomers({
                search: query
            }).then(response => {
                filteredCustomers.value = response.data.data || []
                showCustomerDropdown.value = true
            }).catch(error => {
                console.error('Erreur lors de la recherche des clients:', error)
                filteredCustomers.value = []
            })
        }

        const selectCustomer = (customer) => {
            selectedCustomer.value = customer
            customerSearchQuery.value = customer.name
            showCustomerDropdown.value = false
        }

        const clearCustomerSearch = () => {
            customerSearchQuery.value = ''
            selectedCustomer.value = null
            showCustomerDropdown.value = false
            nextTick(() => {
                customerSearchInput.value?.focus()
            })
        }

        const clearCustomerSelection = () => {
            selectedCustomer.value = null
            customerSearchQuery.value = ''
            nextTick(() => {
                customerSearchInput.value?.focus()
            })
        }

        const validateSale = async () => {
            if (isProcessing.value || !canValidate.value) return

            if (!canValidate.value) {
                toastStore.addToast({
                    type: 'warning',
                    message: 'Veuillez sélectionner ou créer un client',
                    duration: 3000
                })
                focusCustomer()
                return
            }

            isProcessing.value = true

            try {
                const items = cart.value.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity,
                    price_excl_tax: item.price_excl_tax
                }))

                const saleData = {
                    items,
                    discount: discount.value,
                    is_deferred: true,
                    cash_register_id: activeCashRegister.value?.id
                }

                if (customerType.value === 'existing' && selectedCustomer.value) {
                    saleData.customer_id = selectedCustomer.value.id
                } else if (customerType.value === 'new' && newCustomer.value.name) {
                    saleData.customer = {
                        name: newCustomer.value.name,
                        phone: newCustomer.value.phone || null,
                        email: newCustomer.value.email || null
                    }
                }

                const response = await salesService.createSale(saleData)
                completedSale.value = response.data.sale || {}
                showSuccessModal.value = true

                toastStore.addToast({
                    type: 'success',
                    message: 'Vente différée créée avec succès',
                    duration: 4000
                })
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: error.message || 'Erreur lors de la création de la vente',
                    duration: 4000
                })
            } finally {
                isProcessing.value = false
            }
        }

        const printReceipt = async (saleId) => {
            try {
                await salesService.downloadInvoice(saleId)
                toastStore.addToast({
                    type: 'success',
                    message: 'Téléchargement de la facture en cours...',
                    duration: 3000
                })
            } catch (err) {
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du téléchargement de la facture',
                    duration: 4000
                })
            }
        }

        const newSale = () => {
            clearCart()
            selectedCustomer.value = null
            newCustomer.value = { name: '', phone: '', email: '' }
            customerSearchQuery.value = ''
            customerType.value = 'existing'
            searchQuery.value = ''
            selectedCategory.value = ''
            discount.value = 0
            showSuccessModal.value = false
            loadProducts()
            nextTick(() => {
                searchInput.value?.focus()
            })
        }

        const printTicket = async (sale) => {
            try {
                await thermalPrinterService.printTicket(sale, {
                    connectionType: 'usb',
                    shopInfo: JSON.parse(localStorage.getItem('company'))
                })
                toastStore.addToast({
                    type: 'success',
                    message: 'Ticket envoyé à l’imprimante',
                    duration: 3000
                })
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: error.message || 'Erreur lors de l\'impression du ticket',
                    duration: 4000
                })
            }
        }

        const closeSuccessModal = () => {
            showSuccessModal.value = false
        }

        const goBack = () => {
            router.back()
        }

        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(amount || 0)
        }

        const getStockClass = (quantity) => {
            if (quantity === 0 || quantity === null || quantity === undefined) return 'out-of-stock'
            if (quantity <= 5) return 'low-stock'
            return 'in-stock'
        }

        onMounted(async () => {
            await Promise.all([loadProducts(), loadCategories(), loadCustomers(), loadActiveCashRegister()])
            nextTick(() => {
                searchInput.value?.focus()
            })
        })

        return {
            loading,
            products,
            categories,
            searchQuery,
            selectedCategory,
            cart,
            discount,
            isProcessing,
            showSuccessModal,
            completedSale,
            customerType,
            selectedCustomer,
            newCustomer,
            customerSearchQuery,
            filteredCustomers,
            showCustomerDropdown,
            highlightedProductIndex,
            searchInput,
            customerSection,
            customerSearchInput,
            newCustomerNameInput,
            filteredProducts,
            subtotal,
            total,
            canValidate,
            searchProducts,
            clearSearch,
            navigateProducts,
            selectHighlightedProduct,
            filterByCategory,
            addToCart,
            updateItemTotal,
            incrementQuantity,
            decrementQuantity,
            removeFromCart,
            clearCart,
            calculateTotal,
            focusCustomer,
            onUnitPriceInput,
            selectCustomerType,
            searchCustomers,
            selectCustomer,
            clearCustomerSearch,
            clearCustomerSelection,
            validateSale,
            printReceipt,
            printTicket,
            newSale,
            closeSuccessModal,
            goBack,
            formatCurrency,
            getStockClass
        }
    }
})
</script>

<style scoped>
.sale-differe-view {
    padding: 0 0 40px 0;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.page-title {
    display: flex;
    flex-direction: column;
}

.page-heading {
    font-size: 28px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px 0;
}

.page-subtitle {
    color: #64748b;
    font-size: 14px;
    margin: 0;
}

.flex {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
}

.back-btn {
    background-color: transparent;
    border: 1.5px solid #e2e8f0;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    color: #475569;
    cursor: pointer;
}

.validate-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    text-wrap: nowrap;
    width: max-content;
    padding: 10px 20px;
    background-color: #0A5C6B;
    color: white;
    border: 1.5px solid #0A5C6B;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
}

.validate-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.sale-container {
    display: flex;
    gap: 20px;
    height: calc(100vh - 150px);
    min-height: 600px;
}

.products-column {
    flex: 0.60;
    display: flex;
    flex-direction: column;
    background-color: white;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    overflow: hidden;
    position: relative;
}

.cart-column {
    flex: 0.40;
    display: flex;
    flex-direction: column;
    background-color: white;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    overflow: hidden;
}

.search-container {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    border-bottom: 1.5px solid #f1f5f9;
    background-color: #fafbfc;
}

.search-input-wrapper {
    position: relative;
    flex: 1;
}

.search-input-wrapper svg:first-child {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    padding: 12px 44px 12px 44px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    background-color: #ffffff;
}

.search-input:focus {
    outline: none;
    border-color: #0A5C6B;
    background-color: #f8fafc;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.clear-search-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
}

.categories-filter select {
    padding: 12px 36px 12px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background-color: #ffffff;
    font-size: 14px;
    min-width: 200px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-position: right 12px center;
    background-repeat: no-repeat;
    cursor: pointer;
}

.categories-filter select:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.products-list {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
}

.loading-container,
.empty-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    height: 100%;
}

.loading-container svg,
.empty-results svg {
    color: #cbd5e1;
    margin-bottom: 16px;
}

.loading-container p,
.empty-results p {
    color: #94a3b8;
    margin: 0 0 8px 0;
    font-size: 14px;
}

.clear-search-link {
    background: none;
    border: none;
    color: #0A5C6B;
    text-decoration: underline;
    cursor: pointer;
    font-size: 14px;
    padding: 8px 16px;
    margin-top: 8px;
    border-radius: 6px;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f1f5f9;
    border-top: 3px solid #0A5C6B;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
    margin-right: 8px;
}

.products-table-container {
    width: 100%;
    overflow-x: auto;
}

.products-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
}

.products-table thead {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 10;
}

.products-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid #e2e8f0;
}

.products-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
}

.products-table tbody tr.highlighted {
    background: #f0f9fa;
    border-left: 3px solid #0A5C6B;
}

.products-table td {
    padding: 10px 16px;
    font-size: 14px;
    color: #1e293b;
}

.col-image {
    width: 60px;
}

.col-name {
    min-width: 200px;
}

.col-category {
    width: 140px;
}

.col-stock {
    width: 80px;
    text-align: center;
}

.col-price {
    width: 120px;
}

.col-action {
    width: 60px;
    text-align: center;
}

.product-image-small {
    width: 40px;
    height: 40px;
    background-color: #f8fafc;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #e2e8f0;
}

.product-image-small img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.placeholder-image-small {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #cbd5e1;
}

.product-name-text {
    font-weight: 600;
    color: #1e293b;
    display: block;
    margin-bottom: 2px;
}

.product-reference {
    font-size: 12px;
    color: #94a3b8;
    display: block;
}

.category-badge {
    display: inline-block;
    padding: 4px 10px;
    background: #f1f5f9;
    color: #64748b;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
}

.stock-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
}

.stock-badge.in-stock {
    background: #d1fae5;
    color: #065f46;
}

.stock-badge.low-stock {
    background: #fef3c7;
    color: #92400e;
}

.stock-badge.out-of-stock {
    background: #fee2e2;
    color: #991b1b;
}

.product-price-text {
    font-weight: 700;
    color: #0A5C6B;
    font-size: 15px;
}

.add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: #0A5C6B;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
}

.add-btn svg {
    width: 16px;
    height: 16px;
}

.quick-actions-bar {
    position: sticky;
    bottom: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(to top, #ffffff 0%, #fafbfc 100%);
    border-top: 1.5px solid #e2e8f0;
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
    z-index: 10;
}

.quick-info {
    display: flex;
    align-items: center;
    gap: 16px;
}

.items-count {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
}

.quick-total {
    font-size: 16px;
    color: #0f172a;
    font-weight: 700;
}

.quick-validate-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.cart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1.5px solid #f1f5f9;
    background-color: #fafbfc;
}

.cart-header h2 {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.clear-cart-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background-color: #fef2f2;
    color: #dc2626;
    border: 1.5px solid #fecaca;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

.empty-cart {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    height: 300px;
}

.empty-cart svg {
    color: #cbd5e1;
    margin-bottom: 16px;
}

.empty-cart p {
    color: #94a3b8;
    margin: 0;
    font-size: 14px;
}

.empty-cart-hint {
    font-size: 12px !important;
    color: #cbd5e1 !important;
    margin-top: 8px !important;
}

.cart-items {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    min-height: 200px;
}

.cart-item {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
    border-bottom: 1.5px solid #f8fafc;
    border-radius: 8px;
    margin-bottom: 8px;
}

.item-details {
    flex: 1;
    min-width: 0;
}

.item-name {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 4px;
}

.item-price {
    font-size: 13px;
    color: #64748b;
}

.item-price .price-input {
    width: 90px;
    text-align: right;
    padding: 6px 8px;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    margin-right: 6px;
}

.item-price .price-input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.item-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.item-quantity {
    display: flex;
    align-items: center;
    gap: 8px;
}

.item-quantity input {
    width: 50px;
    text-align: center;
    padding: 6px;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
}

.item-quantity input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.quantity-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background-color: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    color: #64748b;
    cursor: pointer;
}

.item-total {
    font-size: 15px;
    font-weight: 700;
    color: #0A5C6B;
    min-width: 90px;
    text-align: right;
}

.remove-item-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background-color: #fef2f2;
    border: 1.5px solid #fecaca;
    border-radius: 6px;
    color: #dc2626;
    cursor: pointer;
}

.cart-summary {
    padding: 20px;
    border-top: 1.5px solid #f1f5f9;
    background-color: #fafbfc;
    min-height: 300px;
    overflow-y: auto;
}

.customer-section {
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1.5px solid #e2e8f0;
}

.customer-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 12px;
}

.customer-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 16px;
}

.customer-option-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 12px;
    background-color: #ffffff;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
}

.customer-option-btn.active {
    border-color: #0A5C6B;
    background-color: #e0f7fa;
    color: #0A5C6B;
}

.existing-customer-selector {
    margin-top: 12px;
}

.customer-search-container {
    position: relative;
    margin-bottom: 12px;
}

.customer-search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.customer-search-input-wrapper svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    z-index: 1;
}

.customer-search-input {
    width: 100%;
    padding: 10px 40px 10px 40px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    background-color: white;
}

.customer-search-input:focus {
    outline: none;
    border-color: #0A5C6B;
    background-color: #f8fafc;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.customer-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: white;
    border: 1.5px solid #e2e8f0;
    border-top: none;
    border-radius: 0 0 8px 8px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 10;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.customer-option {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid #f1f5f9;
}

.customer-option:last-child {
    border-bottom: none;
}

.customer-info .customer-name {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 2px;
}

.customer-info .customer-phone {
    font-size: 12px;
    color: #64748b;
}

.no-customers-found {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: white;
    border: 1.5px solid #e2e8f0;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 16px;
    text-align: center;
    color: #64748b;
    font-size: 14px;
    z-index: 10;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.selected-customer-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background-color: #e0f7fa;
    border: 1.5px solid #0A5C6B;
    border-radius: 8px;
    margin-top: 12px;
}

.customer-details .customer-name {
    font-size: 14px;
    font-weight: 600;
    color: #0A5C6B;
    margin-bottom: 2px;
}

.customer-details .customer-phone {
    font-size: 12px;
    color: #094854;
}

.remove-customer-btn {
    background: none;
    border: none;
    color: #0A5C6B;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
}

.new-customer-form {
    background-color: #f8fafc;
    padding: 12px;
    border-radius: 8px;
    border: 1.5px solid #e2e8f0;
    margin-top: 12px;
}

.form-group {
    margin-bottom: 12px;
}

.form-group:last-child {
    margin-bottom: 0;
}

.form-group label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
}

.form-group input {
    width: 100%;
    padding: 8px 10px;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
}

.form-group input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
    font-size: 14px;
    color: #64748b;
}

.summary-row:last-child {
    margin-bottom: 0;
}

.subtotal-row {
    padding-bottom: 10px;
}

.subtotal-amount {
    font-weight: 600;
    color: #475569;
}

.total-row {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    padding-top: 14px;
    border-top: 2px solid #e2e8f0;
    margin-bottom: 20px;
}

.total-amount {
    color: #0A5C6B;
    font-size: 20px;
}

.discount-row {
    margin-bottom: 16px;
    padding-bottom: 14px;
}

.discount-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.discount-input {
    position: relative;
    width: 140px;
}

.discount-input input {
    width: 100%;
    padding: 10px 50px 10px 12px;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
}

.discount-input input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.currency-symbol {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;
}

.validate-cart-btn {
    width: max-content;
    text-wrap: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 20px;
    background-color: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
}

.validate-cart-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.modal-container {
    background-color: white;
    border-radius: 12px;
    max-width: 90%;
    width: 500px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    animation: modal-appear 0.25s ease-out;
}

@keyframes modal-appear {
    from {
        opacity: 0;
        transform: scale(0.95);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

.modal-body {
    padding: 32px 24px;
    overflow-y: auto;
}

.modal-body.text-center {
    text-align: center;
}

.modal-footer {
    padding: 16px 24px;
    display: flex;
    justify-content: center;
    gap: 12px;
    border-top: 1.5px solid #f1f5f9;
    background-color: #fafbfc;
}

.success-icon {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    color: #10b981;
}

.success-modal h2 {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 10px 0;
    text-align: center;
}

.success-modal p {
    font-size: 15px;
    color: #64748b;
    margin: 0 0 20px 0;
    text-align: center;
}

.invoice-number {
    font-family: monospace;
    font-weight: 700;
    color: #0A5C6B;
    background: #e0f7fa;
    padding: 2px 8px;
    border-radius: 4px;
}

.success-details {
    background-color: #f8fafc;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-size: 14px;
    color: #64748b;
}

.detail-row:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
}

.detail-value {
    font-weight: 600;
    color: #1e293b;
}

.success-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
    padding: 20px;
    border-radius: 8px;
    margin-top: 20px;
}

.success-total span:first-child {
    font-size: 14px;
    color: #0A5C6B;
    font-weight: 600;
}

.success-amount {
    font-size: 24px;
    font-weight: 700;
    color: #0A5C6B;
}

/* Action Cards */
.action-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    width: 100%;
}

.action-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 20px 16px;
    border-radius: 12px;
    background: white;
    border: 2px solid #e2e8f0;
    cursor: pointer;
}

.action-card:hover {
    border-color: #0A5C6B;
    transform: translateY(-2px);
    box-shadow: 0 8px 16px -4px rgba(10, 92, 107, 0.15);
}

.action-card:active {
    transform: translateY(0);
}

.action-card.primary {
    background: linear-gradient(135deg, #0A5C6B 0%, #0d7a8c 100%);
    border-color: #0A5C6B;
}

.action-card.primary .action-card-label {
    color: white;
}

.action-card-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.invoice-icon {
    background: #eff6ff;
    color: #3b82f6;
}

.ticket-icon {
    background: #fef3c7;
    color: #f59e0b;
}

.new-sale-icon {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.action-card-label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.back-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px;
    background: transparent;
    border: none;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

@media (max-width: 1024px) {
    .sale-container {
        flex-direction: column;
        height: auto;
    }

    .products-column,
    .cart-column {
        width: 100%;
        max-height: none;
    }

    .products-list {
        max-height: 500px;
    }

    .cart-items {
        max-height: 200px;
    }
}

@media (max-width: 768px) {
    .search-container {
        flex-direction: column;
        align-items: stretch;
    }

    .categories-filter select {
        width: 100%;
    }

    .customer-options {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .modal-container {
        width: 95%;
        max-width: none;
    }

    .modal-footer {
        flex-direction: column;
    }

    .modal-footer button {
        width: 100%;
    }

    .page-heading {
        font-size: 22px;
    }
}


.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.modal-container {
    background-color: white;
    border-radius: 16px;
    width: 70%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    animation: modal-appear 0.25s ease-out;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

@keyframes modal-appear {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(10px);
    }

    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

.modal-body {
    padding: 32px 24px 24px;
    overflow-y: auto;
}

.modal-body.text-center {
    text-align: center;
}

.modal-footer {
    padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-top: 1px solid #f1f5f9;
    background-color: #f8fafc;
    border-radius: 0 0 16px 16px;
}

.success-icon {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    color: #10b981;
}

.success-modal h2 {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 10px 0;
    text-align: center;
}

.success-modal p {
    font-size: 15px;
    color: #64748b;
    margin: 0 0 20px 0;
    text-align: center;
}

.invoice-number {
    font-family: monospace;
    font-weight: 700;
    color: #0A5C6B;
    background: #e0f7fa;
    padding: 2px 8px;
    border-radius: 4px;
}

.success-details {
    background-color: #f8fafc;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-size: 14px;
    color: #64748b;
}

.detail-row:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
}

.detail-value {
    font-weight: 600;
    color: #1e293b;
}

.success-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
    padding: 20px;
    border-radius: 8px;
}

.success-total span:first-child {
    font-size: 14px;
    color: #0A5C6B;
    font-weight: 600;
}

.success-amount {
    font-size: 24px;
    font-weight: 700;
    color: #0A5C6B;
}

/* Action Cards */
.action-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.action-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 20px 16px;
    border-radius: 12px;
    background: white;
    border: 2px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;
}

.action-card:hover {
    border-color: #0A5C6B;
    transform: translateY(-2px);
    box-shadow: 0 8px 16px -4px rgba(10, 92, 107, 0.15);
}

.action-card:active {
    transform: translateY(0);
}

.action-card.primary {
    background: linear-gradient(135deg, #0A5C6B 0%, #0d7a8c 100%);
    border-color: #0A5C6B;
}

.action-card.primary:hover {
    background: linear-gradient(135deg, #094854 0%, #0A5C6B 100%);
    border-color: #094854;
    box-shadow: 0 8px 16px -4px rgba(10, 92, 107, 0.3);
}

.action-card-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.invoice-icon {
    background: #eff6ff;
    color: #3b82f6;
}

.ticket-icon {
    background: #fef3c7;
    color: #f59e0b;
}

.new-sale-icon {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.action-card-label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.action-card.primary .action-card-label {
    color: white;
}

.back-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px;
    background: transparent;
    border: none;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.2s;
}

.back-link:hover {
    color: #0A5C6B;
}

@media (max-width: 480px) {
    .modal-container {
        width: 95%;
        max-width: none;
    }

    .action-cards {
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
    }

    .action-card {
        padding: 16px 12px;
    }

    .action-card-icon {
        width: 48px;
        height: 48px;
    }

    .action-card-icon svg {
        width: 24px;
        height: 24px;
    }

    .action-card-label {
        font-size: 11px;
    }
}
</style>